import {
  COMPARE_STORAGE_KEY,
  MAX_COMPARE_ITEMS,
  decodeHtmlEntities,
  readStoredItems,
  type StoredCompareItem,
} from "./compare-constants";

const writeStoredItems = (items: StoredCompareItem[]): void => {
  try {
    localStorage.setItem(COMPARE_STORAGE_KEY, JSON.stringify(items));
    window.dispatchEvent(new CustomEvent("carbatec:compare-changed"));
  } catch (error) {
    console.error(
      "[compare] Failed to write compare list to localStorage",
      error,
    );
  }
};

const CARD_WRAPPER_SELECTOR =
  // Searchspring sometimes renders card wrapper with legacy underscore class.
  ".card-wrapper, .card_wrapper";

const getProductUrlFromCard = (card: HTMLElement | null): string => {
  if (!card) return "";
  return (
    card.getAttribute("data-product-url") ||
    card
      .querySelector<HTMLElement>("[data-product-url]")
      ?.getAttribute("data-product-url") ||
    ""
  );
};

const findCardImageByProductUrl = (
  productUrl: string,
): HTMLImageElement | null => {
  const productUrlEls = document.querySelectorAll<HTMLElement>(
    ".card-wrapper[data-product-url], .card_wrapper[data-product-url], .card-wrapper [data-product-url], .card_wrapper [data-product-url]",
  );

  for (const urlEl of productUrlEls) {
    const url = urlEl.getAttribute("data-product-url") || "";
    if (url !== productUrl) continue;

    const card = urlEl.closest<HTMLElement>(CARD_WRAPPER_SELECTOR);
    const img = (card ?? urlEl).querySelector<HTMLImageElement>("img");
    if (img) return img;
  }
  return null;
};

const toggleCompareUrl = (productUrl: string): boolean | null => {
  const items = readStoredItems();
  const index = items.findIndex((item) => item.url === productUrl);

  if (index !== -1) {
    items.splice(index, 1);
    writeStoredItems(items);
    return false;
  }

  if (items.length >= MAX_COMPARE_ITEMS) return null;
  const img: HTMLImageElement | null =
    findCardImageByProductUrl(productUrl) ||
    document.querySelector<HTMLImageElement>(".product__media-wrapper img");

  items.push({
    url: productUrl,
    imageSrc: img ? img.currentSrc || img.src : undefined,
  });
  writeStoredItems(items);
  return true;
};

const syncCompareCheckboxes = (root: ParentNode): void => {
  const compareUrls = readStoredItems().map((item) => item.url);

  root
    .querySelectorAll<HTMLInputElement>(".compare-checkbox")
    .forEach((checkbox) => {
      const card = checkbox.closest<HTMLElement>(CARD_WRAPPER_SELECTOR);
      const url = getProductUrlFromCard(card);
      checkbox.checked = !!url && compareUrls.includes(url);
    });

  root
    .querySelectorAll<HTMLButtonElement>(".compare-pdp-btn")
    .forEach((button) => {
      const url = button.getAttribute("data-product-url") || "";
      const isActive = !!url && compareUrls.includes(url);
      button.classList.toggle("compare-pdp-btn--active", isActive);
    });
};

const updateCompareBar = (): void => {
  const bar = document.getElementById("compare-bar");
  if (!bar) return;

  const comparePageUrl =
    bar.getAttribute("data-compare-page-url") || "/pages/compare";

  if (location.pathname.startsWith("/pages/compare")) {
    bar.hidden = true;
    return;
  }

  const items = readStoredItems().slice(0, MAX_COMPARE_ITEMS);
  const urls = items.map((item) => item.url);
  if (!urls.length) {
    bar.hidden = true;
    const itemsEl = bar.querySelector<HTMLElement>(".compare-bar__items");
    if (itemsEl) itemsEl.replaceChildren();
    const linkEl = bar.querySelector<HTMLAnchorElement>(".compare-bar__cta");
    if (linkEl) linkEl.href = comparePageUrl;
    return;
  }

  const handles = urls
    .map((u) => /\/products\/([^/?]+)/.exec(u)?.[1])
    .filter((x): x is string => Boolean(x))
    .slice(0, MAX_COMPARE_ITEMS);

  const getBarText = (attr: string, fallback: string): string =>
    decodeHtmlEntities(bar.getAttribute(attr) || fallback);

  const baseUrl = comparePageUrl;
  bar.hidden = false;

  const itemsEl = bar.querySelector<HTMLElement>(".compare-bar__items");
  if (itemsEl) {
    itemsEl.replaceChildren();
    for (let i = 0; i < MAX_COMPARE_ITEMS; i++) {
      const item = items[i];
      if (!item) {
        const placeholder = document.createElement("div");
        placeholder.className = "compare-bar__thumb compare-bar__thumb--empty";
        itemsEl.appendChild(placeholder);
        continue;
      }

      let src = item.imageSrc;

      if (!src) {
        const productImgFromCard = findCardImageByProductUrl(item.url);
        const productImg =
          productImgFromCard ||
          (location.pathname === item.url
            ? document.querySelector<HTMLImageElement>(
                ".product__media-wrapper img",
              )
            : null);
        if (productImg) {
          src = productImg.currentSrc || productImg.src;
        }
      }

      if (!src) {
        const placeholder = document.createElement("div");
        placeholder.className = "compare-bar__thumb compare-bar__thumb--empty";
        itemsEl.appendChild(placeholder);
        continue;
      }

      const thumbWrap = document.createElement("div");
      thumbWrap.className = "compare-bar__thumb-wrap";

      const thumbImg = document.createElement("img");
      thumbImg.src = src;
      thumbImg.alt = "";
      thumbImg.loading = "lazy";
      thumbImg.className = "compare-bar__thumb";
      thumbWrap.appendChild(thumbImg);

      const removeBtn = document.createElement("button");
      removeBtn.type = "button";
      removeBtn.className = "compare-bar__thumb-remove";
      removeBtn.setAttribute("aria-label", "Remove from comparison");
      removeBtn.innerHTML =
        '<svg width="16" height="16" viewBox="0 0 16 17" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M8 3.4259V12.7592" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/><path d="M3.33325 8.09253H12.6666" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      removeBtn.addEventListener("click", () => {
        const result = toggleCompareUrl(item.url);
        if (result !== null) {
          syncCompareCheckboxes(document);
        }
      });
      thumbWrap.appendChild(removeBtn);

      itemsEl.appendChild(thumbWrap);
    }
  }

  const linkEl = bar.querySelector<HTMLAnchorElement>(".compare-bar__cta");
  const clearButtonEl = bar.querySelector<HTMLButtonElement>(".compare-bar__clear");
  if (linkEl) {
    linkEl.textContent = getBarText("data-compare-cta", "Compare");
    const fromUrl = encodeURIComponent(window.location.pathname);
    const fromTitle = encodeURIComponent(document.title);
    linkEl.href = handles.length
      ? baseUrl +
        "?" +
        handles.map((h) => "handle=" + encodeURIComponent(h)).join("&") +
        "&from_url=" + fromUrl +
        "&from_title=" + fromTitle
      : baseUrl;
    linkEl.hidden = urls.length <= 1;
  }

  if (clearButtonEl) {
    clearButtonEl.hidden = urls.length <= 1;
  }
};

let compareAlertTimeout: number | null = null;

const showCompareLimitAlert = (message: string): void => {
  const alertEl = document.getElementById("compare-bar-alert");
  if (!(alertEl instanceof HTMLDivElement)) return;

  const msgEl =
    alertEl.querySelector<HTMLParagraphElement>(".compare-bar-alert__message");
  if (msgEl) msgEl.textContent = message;

  const closeBtn = alertEl.querySelector(".compare-bar-alert__close");
  if (closeBtn instanceof HTMLButtonElement && !closeBtn.dataset.bound) {
    closeBtn.dataset.bound = "true";
    closeBtn.addEventListener("click", () => {
      alertEl.style.display = "none";
    });
  }

  alertEl.style.display = "flex";

  if (compareAlertTimeout !== null) {
    window.clearTimeout(compareAlertTimeout);
  }
  compareAlertTimeout = window.setTimeout(() => {
    alertEl.style.display = "none";
  }, 4000);
};

const handleCompareClick = (event: MouseEvent): void => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const trigger = target.closest(
    ".compare-checkbox, .compare-checkbox-label, .compare-pdp-btn",
  );
  if (!trigger) return;

  const checkboxCandidate =
    trigger instanceof HTMLInputElement
      ? trigger
      : trigger.querySelector(".compare-checkbox");
  const checkbox =
    checkboxCandidate instanceof HTMLInputElement ? checkboxCandidate : null;

  const button = checkbox ? null : trigger.closest(".compare-pdp-btn");
  const pdpButton = button instanceof HTMLButtonElement ? button : null;

  let url = "";
  if (checkbox) {
    const card = checkbox.closest<HTMLElement>(CARD_WRAPPER_SELECTOR);
    url = getProductUrlFromCard(card);
  } else {
    url = pdpButton?.getAttribute("data-product-url") || "";
  }
  if (!url) return;
  event.preventDefault();

  const result = toggleCompareUrl(url);
  if (result === null) {
    const message =
      decodeHtmlEntities(document.body.getAttribute("data-label-compare-limit") || "") ||
      "Compare limit reached. You can only compare a maximum of 3 products. To compare this product, please remove another first.";
    showCompareLimitAlert(message);
    return;
  }
  if (checkbox) checkbox.checked = result;
  syncCompareCheckboxes(document);
};

let compareInitialized = false;

const initCompare = (root: ParentNode): void => {
  if (!compareInitialized) {
    document.body.addEventListener("click", handleCompareClick);
    window.addEventListener("carbatec:compare-changed", updateCompareBar);
    window.addEventListener("storage", (event) => {
      if (event.key === COMPARE_STORAGE_KEY) updateCompareBar();
    });
    compareInitialized = true;
  }
  syncCompareCheckboxes(root);
  updateCompareBar();

  const clearButtonEl = document.querySelector<HTMLButtonElement>(
    ".compare-bar__clear",
  );
  if (clearButtonEl && !clearButtonEl.dataset.bound) {
    clearButtonEl.dataset.bound = "true";
    clearButtonEl.addEventListener("click", () => {
      writeStoredItems([]);
      syncCompareCheckboxes(document);
    });
  }
};

initCompare(document);
document.addEventListener("shopify:section:load", (event) => {
  if (event.target instanceof HTMLElement) initCompare(event.target);
});
